import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "glamor";
import { ArrowUp, Bell, ChevronDown, MessageSquare, FileText } from "lucide-react";
import {
  getUnreadNotifications,
  getReadNotifications,
} from "../../selectors/notificationSelectors";
import { getNotificationsUserByNotificationId } from "../../selectors/notificationsUserSelectors";
import {
  readNotification,
  readAllNotifications,
  unreadNotification,
} from "../../processes/notificationsUserProcesses";
import Page from "../library/Page";

const PER_PAGE = 10;

// Horizontal padding that AppLayout.main applies (must mirror AppLayout.mainStyle).
// Used for negative-margin escapes so header, tab bar and content fill the full inset.
const MAIN_H_PAD = 32;
const MAIN_T_PAD = 16;

// ── Page header — escapes main padding, same look on all screen sizes ──

const pageHeaderClass = css({
  margin: `-${MAIN_T_PAD}px -${MAIN_H_PAD}px 0`,
  padding: "24px 32px 16px",
  minHeight: 104,
  background: "#FFFFFF",
  borderBottom: "0.6px solid #E2E8F0",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontFamily: "Montserrat, sans-serif",
  "@media(max-width: 480px)": {
    padding: "18px 16px 14px",
  },
});

const pageTitleRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const pageTitleClass = css({
  // !important to beat the global `h1..h6 { font-family: FamiliarPro !important }` reset.
  fontFamily: "Montserrat, sans-serif !important",
  fontSize: 20,
  fontWeight: 600,
  lineHeight: "30px",
  color: "#0F172A",
  margin: 0,
  "@media(max-width: 480px)": {
    fontSize: 18,
    lineHeight: "28px",
  },
});

const pageSubtitleClass = css({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "21px",
  color: "#64748B",
  margin: 0,
  "@media(max-width: 480px)": {
    fontSize: 13,
    lineHeight: "20px",
  },
});

// ── Content wrapper — also escapes main padding for the gray bg ──

const contentClass = css({
  margin: `0 -${MAIN_H_PAD}px -${MAIN_H_PAD}px`,
  padding: `24px ${MAIN_H_PAD}px 40px`,
  background: "#F8FAFC",
  minHeight: "calc(100vh - 104px)",
  display: "flex",
  flexDirection: "column",
  gap: 32,
  fontFamily: "Montserrat, sans-serif",
  "@media(max-width: 640px)": {
    padding: "18px 16px 32px",
    margin: `0 -${MAIN_H_PAD}px`,
    gap: 28,
  },
});

// ── Section label ─────────────────────────────────────────────

const sectionClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  width: "100%",
});

const sectionTitleClass = css({
  display: "flex",
  alignItems: "center",
  gap: 6,
  minHeight: 18,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
  letterSpacing: 0.5,
  textTransform: "uppercase",
  color: "#94A3B8",
});

const sectionToggleClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  width: "fit-content",
  minHeight: 18,
  padding: 0,
  border: 0,
  background: "transparent",
  color: "#94A3B8",
  cursor: "pointer",
  fontFamily: "inherit",
});

const chevronClass = css({
  transition: "transform 0.15s ease",
  flexShrink: 0,
});

const chevronCollapsedClass = css({
  transform: "rotate(-90deg)",
});

const sectionHeaderRowClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
});

const markAllReadBtnClass = css({
  padding: "2px 10px",
  height: 24,
  border: "1px solid #CBD5E1",
  borderRadius: 6,
  background: "transparent",
  color: "#475569",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: "18px",
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
  ":hover": { background: "#F1F5F9" },
});

// ── Notification card ─────────────────────────────────────────

const cardBaseClass = css({
  width: "100%",
  padding: 21,
  background: "#FFFFFF",
  borderRadius: 10,
  display: "flex",
  flexDirection: "column",
  gap: 10,
  position: "relative",
  "@media(max-width: 480px)": {
    padding: 16,
  },
});

// borderColor !important to beat the global `.lms-modern-chrome` border reset.
const cardUnreadClass = css({
  border: "1px solid #BAE6FD",
  borderColor: "#BAE6FD !important",
});
const cardReadClass = css({
  minHeight: 88,
  border: "1px solid #E2E8F0",
  opacity: 0.7,
  "@media(max-width: 480px)": { minHeight: 0, paddingBottom: 16 },
});

// card top row: flex on desktop, grid on mobile
const cardTopClass = css({
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  width: "100%",
  "@media(max-width: 480px)": {
    display: "grid",
    gridTemplateColumns: "40px minmax(0, 1fr)",
    gap: 12,
    paddingTop: 16,
  },
});

const iconBaseClass = css({
  width: 40,
  height: 40,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "@media(max-width: 480px)": {
    gridColumn: 1,
    gridRow: 1,
  },
});

const iconUnreadClass = css({
  background: "#F0F9FF",
  border: "1px solid #BAE6FD",
  borderColor: "#BAE6FD !important",
  color: "#0369A1",
});
const iconReadClass = css({
  background: "#F1F5F9",
  border: "none",
  color: "#94A3B8",
});

const copyClass = css({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  "@media(max-width: 480px)": {
    gridColumn: 2,
    gridRow: 1,
  },
});

const cardTitleStyle = {
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "22px",
  color: "#0F172A",
  margin: 0,
  fontFamily: "'Montserrat:Medium', sans-serif",
};

const cardTitleReadStyle = { ...cardTitleStyle, color: "#475569" };

const cardMetaStyle = {
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "18px",
  color: "#94A3B8",
  margin: 0,
};

// Description line — shown only when the notification carries a body (text).
const cardDescriptionStyle = {
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "18px",
  color: "#94a3b8",
  margin: 0,
  whiteSpace: "pre-line",
};

// Unread blue dot — absolute on mobile, inline on desktop
const dotClass = css({
  width: 7,
  height: 7,
  borderRadius: 999,
  background: "#0369A1",
  flexShrink: 0,
  "@media(max-width: 480px)": {
    position: "absolute",
    top: 13,
    right: 106,
    marginTop: 0,
  },
});

// "Marcar como lida" — absolute on mobile, inline on desktop
const readBtnClass = css({
  height: 24,
  padding: "0 8px",
  border: 0,
  borderRadius: 6,
  background: "transparent",
  color: "#0369A1",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: "16px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  ":hover": { background: "#F0F9FF" },
  "@media(max-width: 480px)": {
    position: "absolute",
    top: 6,
    right: 12,
    fontSize: 10,
    height: 22,
    padding: "0 4px",
    lineHeight: "15px",
  },
});

const unreadBtnClass = css({
  height: 24,
  padding: "0 8px",
  border: 0,
  borderRadius: 6,
  background: "transparent",
  color: "#0369A1",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: "16px",
  whiteSpace: "nowrap",
  flexShrink: 0,
  ":hover": { background: "#F0F9FF" },
  "@media(max-width: 480px)": {
    position: "absolute",
    top: 6,
    right: 12,
    fontSize: 10,
    height: 22,
    padding: "0 4px",
    lineHeight: "15px",
  },
});

const actionsClass = css({
  display: "flex",
  alignItems: "center",
  gap: 8,
  paddingLeft: 50,
  flexWrap: "wrap",
  "@media(max-width: 480px)": { paddingLeft: 50 },
});

const actionBtnClass = css({
  minHeight: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  padding: "0 14px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  color: "#64748B",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  whiteSpace: "nowrap",
  fontFamily: "'Montserrat:Medium', sans-serif",
  textDecoration: "none",
});

const NOTIFICATION_TYPE_LABEL = {
  notice: "Informativo",
};

function NotificationIcon({ isUnread, notificationType, ctaButtonText }) {
  const Icon =
    ctaButtonText || notificationType !== "notice" ? FileText : MessageSquare;
  return (
    <div
      className={css(iconBaseClass, isUnread ? iconUnreadClass : iconReadClass)}
    >
      <Icon size={18} strokeWidth={1.5} />
    </div>
  );
}

function NotificationItem({
  notification,
  notificationsUser,
  isUnread,
  onMarkAsRead,
  onMarkAsUnread,
}) {
  const handleMarkAsRead = useCallback(() => {
    if (isUnread && notificationsUser?.id) {
      onMarkAsRead(notificationsUser.id);
    }
  }, [isUnread, notificationsUser, onMarkAsRead]);

  const handleMarkAsUnread = useCallback(() => {
    if (!isUnread && notificationsUser?.id) {
      onMarkAsUnread(notificationsUser.id);
    }
  }, [isUnread, notificationsUser, onMarkAsUnread]);

  const typeLabel = NOTIFICATION_TYPE_LABEL[notification.notificationType];
  const isNotice = notification.notificationType === "notice";
  const hasDescription = Boolean(notification.text && notification.text.trim());

  return (
    <article
      className={css(cardBaseClass, isUnread ? cardUnreadClass : cardReadClass)}
    >
      <div className={cardTopClass}>
        <NotificationIcon
          isUnread={isUnread}
          notificationType={notification.notificationType}
          ctaButtonText={notification.ctaButtonText}
        />
        <div className={copyClass}>
          <p style={isUnread ? cardTitleStyle : cardTitleReadStyle}>
            {notification.title}
          </p>
          {hasDescription ? (
            <p style={cardDescriptionStyle}>{notification.text}</p>
          ) : (
            typeLabel && <p style={cardMetaStyle}>{typeLabel}</p>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {isUnread && <span className={dotClass} />}
          {isUnread && (
            <button
              type="button"
              className={readBtnClass}
              onClick={handleMarkAsRead}
            >
              Marcar como lida
            </button>
          )}
          {!isUnread && (
            <button
              type="button"
              className={unreadBtnClass}
              onClick={handleMarkAsUnread}
            >
              Marcar como não lida
            </button>
          )}
        </div>
      </div>
      {isUnread &&
        notification.url &&
        (notification.ctaButtonText || !isNotice) && (
          <div className={actionsClass}>
            <a
              className={actionBtnClass}
              href={notification.url}
              target={notification.isCta ? "_blank" : undefined}
              rel="noreferrer"
            >
              {notification.ctaButtonText && (
                <FileText size={14} strokeWidth={1.5} />
              )}
              {notification.ctaButtonText || "Abrir"}
            </a>
          </div>
        )}
    </article>
  );
}

function ConnectedNotificationItem({ notification, isUnread, dispatch }) {
  const handleMarkAsRead = useCallback(
    (notificationsUserId) => readNotification(dispatch, notificationsUserId),
    [dispatch],
  );
  const handleMarkAsUnread = useCallback(
    (notificationsUserId) => unreadNotification(dispatch, notificationsUserId),
    [dispatch],
  );
  return (
    <ConnectedNotificationItemInner
      notification={notification}
      isUnread={isUnread}
      onMarkAsRead={handleMarkAsRead}
      onMarkAsUnread={handleMarkAsUnread}
    />
  );
}

const ConnectedNotificationItemInner = connect((state, ownProps) => ({
  notificationsUser: getNotificationsUserByNotificationId(
    state,
    ownProps.notification.id,
  ),
}))(NotificationItem);

// ── Empty state ───────────────────────────────────────────────

const emptyPanelClass = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "60px 20px",
  minHeight: 359,
  "@media(max-width: 480px)": {
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "76px 16px 48px",
    minHeight: 430,
  },
});

const emptyIconWrapStyle = {
  width: 56,
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#F1F5F9",
  borderRadius: 999,
  marginBottom: 16,
};

const emptyTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "24px",
  color: "#475569",
  margin: "0 0 4px",
};
const emptyDescStyle = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "20px",
  color: "#94A3B8",
  margin: 0,
  maxWidth: 260,
};

function EmptyState({ title, description }) {
  return (
    <div className={emptyPanelClass}>
      <div style={emptyIconWrapStyle}>
        <Bell size={24} strokeWidth={1.5} color="#94A3B8" />
      </div>
      <p style={emptyTitleStyle}>{title}</p>
      <p style={emptyDescStyle}>{description}</p>
    </div>
  );
}

// ── Back to top ───────────────────────────────────────────────

const backToTopClass = css({
  position: "fixed",
  bottom: 32,
  right: 32,
  width: 44,
  height: 44,
  borderRadius: "50%",
  background: "#0369A1",
  border: "none",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(3, 105, 161, 0.35)",
  transition: "opacity 0.2s ease, transform 0.2s ease",
  zIndex: 50,
  ":hover": {
    background: "#075985",
  },
  "@media(max-width: 640px)": {
    bottom: 20,
    right: 16,
  },
});

const backToTopHiddenClass = css({
  opacity: 0,
  pointerEvents: "none",
  transform: "translateY(8px)",
});

function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      type="button"
      aria-label="Voltar ao topo"
      className={`${backToTopClass} ${!visible ? backToTopHiddenClass : ""}`}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <ArrowUp size={20} strokeWidth={2} />
    </button>
  );
}

// ── Pagination ────────────────────────────────────────────────

const paginationClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minHeight: 40,
  paddingTop: 8,
  flexWrap: "wrap",
});

const paginationBtnBase = {
  height: 32,
  minWidth: 32,
  padding: "0 13px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  color: "#475569",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "18px",
  fontFamily: "inherit",
};
const paginationBtnActive = {
  ...paginationBtnBase,
  background: "#0369A1",
  borderColor: "#0369A1",
  color: "#FFFFFF",
};
const paginationBtnDisabled = {
  ...paginationBtnBase,
  opacity: 0.4,
  cursor: "default",
};

function Pagination({ currentPage, totalPages, onPageChange }) {
  const handlePrev = useCallback(
    () => onPageChange(currentPage - 1),
    [currentPage, onPageChange],
  );
  const handleNext = useCallback(
    () => onPageChange(currentPage + 1),
    [currentPage, onPageChange],
  );

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  if (totalPages <= 1) return null;

  return (
    <nav className={paginationClass} aria-label="Paginação">
      <button
        type="button"
        onClick={isFirst ? undefined : handlePrev}
        disabled={isFirst}
        style={isFirst ? paginationBtnDisabled : paginationBtnBase}
      >
        Anterior
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          style={page === currentPage ? paginationBtnActive : paginationBtnBase}
        >
          {page}
        </button>
      ))}
      <button
        type="button"
        onClick={isLast ? undefined : handleNext}
        disabled={isLast}
        style={isLast ? paginationBtnDisabled : paginationBtnBase}
      >
        Próxima
      </button>
    </nav>
  );
}

// ── Main page ─────────────────────────────────────────────────

const listClass = css({ display: "flex", flexDirection: "column", gap: 12 });

function NotificationsPage({
  dispatch,
  unreadNotifications = [],
  readNotifications = [],
}) {
  const [pendingPage, setPendingPage] = useState(1);
  const [readPage, setReadPage] = useState(1);
  const [isReadCollapsed, setIsReadCollapsed] = useState(true);

  useEffect(() => {
    if (unreadNotifications.length === 0 && readNotifications.length > 0) {
      setIsReadCollapsed(false);
    }
  }, [unreadNotifications.length, readNotifications.length]);

  const pendingTotalPages = Math.ceil(unreadNotifications.length / PER_PAGE);
  const pendingPageItems = unreadNotifications.slice(
    (pendingPage - 1) * PER_PAGE,
    pendingPage * PER_PAGE,
  );
  const readTotalPages = Math.ceil(readNotifications.length / PER_PAGE);
  const readPageItems = readNotifications.slice(
    (readPage - 1) * PER_PAGE,
    readPage * PER_PAGE,
  );

  const handlePendingPageChange = useCallback((page) => {
    setPendingPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleReadPageChange = useCallback((page) => {
    setReadPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleMarkAllAsRead = useCallback(() => {
    readAllNotifications(dispatch);
  }, [dispatch]);

  const handleReadToggle = useCallback(() => {
    setIsReadCollapsed((collapsed) => !collapsed);
  }, []);

  return (
    <>
    <Page>
      <header className={pageHeaderClass}>
        <div style={pageTitleRowStyle}>
          <Bell size={20} strokeWidth={1.666} color="#0F172A" />
          <h1 className={pageTitleClass}>Notificações</h1>
        </div>
        <p className={pageSubtitleClass}>
          Acompanhe avisos, solicitações e atualizações
        </p>
      </header>

      <div className={contentClass}>
        {unreadNotifications.length === 0 && readNotifications.length === 0 ? (
          <EmptyState
            title="Nenhuma notificação"
            description="Quando houver algo novo, aparecerá aqui."
          />
        ) : (
        <>
        <section className={sectionClass}>
          <div className={sectionHeaderRowClass}>
            <div className={sectionTitleClass}>
              Pendentes ({unreadNotifications.length})
            </div>
            {unreadNotifications.length > 0 && (
              <button
                type="button"
                className={markAllReadBtnClass}
                onClick={handleMarkAllAsRead}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
          {unreadNotifications.length > 0 && (
            <>
              <div className={listClass}>
                {pendingPageItems.map((n) => (
                  <ConnectedNotificationItem
                    key={n.id}
                    notification={n}
                    isUnread
                    dispatch={dispatch}
                  />
                ))}
              </div>
              <Pagination
                currentPage={pendingPage}
                totalPages={pendingTotalPages}
                onPageChange={handlePendingPageChange}
              />
            </>
          )}
        </section>

        <section className={sectionClass}>
          {readNotifications.length > 0 ? (
            <button
              type="button"
              className={css(sectionTitleClass, sectionToggleClass)}
              onClick={handleReadToggle}
              aria-expanded={!isReadCollapsed}
              aria-controls="read-notifications-list"
            >
              Lidas ({readNotifications.length})
              <ChevronDown
                size={14}
                strokeWidth={2}
                className={`${css(chevronClass, isReadCollapsed && chevronCollapsedClass)}`}
              />
            </button>
          ) : (
            <div className={sectionTitleClass}>
              Lidas ({readNotifications.length})
            </div>
          )}
          {!isReadCollapsed && readNotifications.length > 0 && (
            <>
              <div id="read-notifications-list" className={listClass}>
                {readPageItems.map((n) => (
                  <ConnectedNotificationItem
                    key={n.id}
                    notification={n}
                    isUnread={false}
                    dispatch={dispatch}
                  />
                ))}
              </div>
              <Pagination
                currentPage={readPage}
                totalPages={readTotalPages}
                onPageChange={handleReadPageChange}
              />
            </>
          )}
        </section>
        </>
        )}
      </div>
    </Page>
    <BackToTop />
    </>
  );
}

function mapStateToProps(state) {
  return {
    unreadNotifications: getUnreadNotifications(state) || [],
    readNotifications: getReadNotifications(state) || [],
  };
}

export default connect(mapStateToProps)(NotificationsPage);
